import React, { useState } from 'react';
import { useTranslate, useRefresh } from 'react-admin';
import { SvgIcon } from '@material-ui/core';
import { EditButton, TextInput } from '~/components';
import { useNotify } from '~/hooks';
import Confirmation from './Confirmation';
import { Grid } from '@material-ui/core';
import { updateEventLog } from '~/ducks/eventLog';
import { useDispatch } from 'react-redux';
import { useUpdateIndividualCustomerNumber } from '~/hooks/individuals';
import { useUpdateBusinessCustomerNumber } from '~/hooks/businesses';
import { Alert } from '@material-ui/lab';
import ContactsIcon from '@material-ui/icons/Contacts';

const label = (key: string): string => `components.UpdateCustomerNumber.${key}`;

interface Props {
  profileId: string;
  profileType: 'INDIVIDUAL' | 'BUSINESS';
}

const UpdateCustomerNumber = (props: Props) => {
  const { profileId, profileType } = props;

  const [customerNumber, setCustomerNumber] = useState<string>();
  const [note, setNote] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<{ customerNumber?: string; note?: string }>({});

  const handleNote = ({ target }: any) => {
    setErrorMessage((prev) => ({ ...prev, note: undefined }));
    setNote(target?.value);
  };

  const handleCustomerNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage((prev) => ({ ...prev, customerNumber: undefined }));
    setCustomerNumber(event.target.value);
  };

  const [, setOpen] = useState(false);
  const openDialog = () => {
    setOpen(true);
  };

  const translate = useTranslate();
  const t = (key: string): string => translate(label(key));

  const notify = useNotify();
  const dispatch = useDispatch();
  const forceRefresh = useRefresh();

  const updateIndividualMutation = useUpdateIndividualCustomerNumber(profileId);
  const updateBusinessMutation = useUpdateBusinessCustomerNumber(profileId);

  const handleProfileMutation =
    profileType === 'INDIVIDUAL' ? updateIndividualMutation : updateBusinessMutation;

  const onConfirm = async () => {
    let errors: { customerNumber?: string; note?: string } = {};
    if (!note || !customerNumber) {
      if (!customerNumber) errors.customerNumber = translate('ra.validation.required');
      if (!note) errors.note = translate('ra.validation.required');
      setErrorMessage(errors);
      throw new Error();
    } else if (note && customerNumber) {
      await handleProfileMutation.mutateAsync({ customerNumber, note });
      forceRefresh();
      dispatch(updateEventLog());
      notify({ type: 'success', message: t('profileCustomerNumberUpdated') });
    }
  };

  return (
    <Confirmation
      onConfirm={onConfirm}
      onClose={() => {
        setCustomerNumber(undefined);
        setNote(undefined);
        setErrorMessage({});
      }}
      confirmButtonProps={{
        loading: handleProfileMutation.isLoading,
      }}
      confirmationSettings={{
        variant: 'modal',
        modal: {
          heading: t('updateCustomerNumber'),
          content: (
            <Grid container spacing={2} style={{ minWidth: 400 }}>
              <Grid item xs={12}>
                <Alert severity='warning'>{t('warning')}</Alert>
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  label={t('customerNumber')}
                  value={customerNumber}
                  onChange={handleCustomerNumber}
                  error={!!errorMessage.customerNumber}
                  helperText={errorMessage.customerNumber}
                  required
                />
                <TextInput
                  label={t('note')}
                  value={note}
                  onChange={handleNote}
                  fullWidth
                  multiline
                  error={!!errorMessage.note}
                  helperText={errorMessage.note}
                  rows={4}
                  required
                />
              </Grid>
            </Grid>
          ),
        },
      }}
    >
      <EditButton
        label={t('updateCustomerNumber')}
        icon={
          <SvgIcon>
            <ContactsIcon />
          </SvgIcon>
        }
        onClick={openDialog}
      />
    </Confirmation>
  );
};

export default UpdateCustomerNumber;
